import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Input, Switch } from 'antd';
import { IThreePsCodeItem } from '../../threePsCodes.model';
import { COLUMN_CONFIG, HIDDEN_COLUMNS } from './threePsCodeTable.constants';
import { INCLUDE_IN_EMAIL, INCLUDE_IN_REPORTING, IS_REVIEW_COMPLETED, UPDATED_BY, UPDATED_ON, USER_DEFINED_DESCRIPTION, USER_DEFINED_EMAIL_DESCRIPTION } from '../../threePsCodes.constants';
import { getRowErrors } from './threePsCodeTable.utils';
import styles from './threePsCodeTable.module.css';
import { Checkbox } from '@mui/material';
import { formatUTCDateTime } from '../../../../utils/formatUTCDate';

type GetThreePsCodeColumnsArgs = {
    isEditMode: boolean;
    attemptedSave?: boolean;
    onFieldChange: (
        id: string,
        field: keyof IThreePsCodeItem,
        value: string | boolean | null
    ) => void;
    emailDescRefs: React.MutableRefObject<
        Record<string, React.ComponentRef<typeof Input> | null>
    >;
    portalDescRefs: React.MutableRefObject<
        Record<string, React.ComponentRef<typeof Input> | null>
    >;
};

const renderToggle = (
    checked: boolean,
    isEditMode: boolean,
    onChange: (checked: boolean) => void
) => (
    <Switch checked={checked} onChange={onChange} size="small" disabled={!isEditMode} />
);

const renderCheckbox = (
    checked: boolean,
    isEditMode: boolean,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
) => (
    <div className={isEditMode ? "" : styles.checkboxContainer}>
        <Checkbox checked={checked} onChange={onChange} disabled={!isEditMode} className={`${styles.checkbox} ${isEditMode ? styles.editableCheckbox : ""}`} />
    </div>
)

export const getThreePsCodeColumns = ({
    isEditMode,
    attemptedSave,
    onFieldChange,
    emailDescRefs,
    portalDescRefs,
}: GetThreePsCodeColumnsArgs): ColumnsType<IThreePsCodeItem> =>
    COLUMN_CONFIG.filter(({ key }) => !HIDDEN_COLUMNS.has(key)).map(
        ({ key, title, width }) => {
            const needsCustomPadding = key === USER_DEFINED_EMAIL_DESCRIPTION || key === USER_DEFINED_DESCRIPTION || key === IS_REVIEW_COMPLETED;

            return ({
                title,
                dataIndex: key,
                key,
                width,
                onCell: () => (needsCustomPadding ? { className: styles.customPaddingCell } : {}),
                render: (value: keyof IThreePsCodeItem, record: IThreePsCodeItem) => {
                    const id = record.threePsDataUid;
                    const showEmailDescError = Boolean(attemptedSave && getRowErrors(record).emailDescHasError);
                    const showPortalDescError = Boolean(attemptedSave && getRowErrors(record).portalDescHasError);
                    
                    switch (key) {

                        case INCLUDE_IN_EMAIL:
                        case INCLUDE_IN_REPORTING:
                            return renderToggle(Boolean(value), isEditMode, (checked) =>
                                onFieldChange(id, key, checked)
                            );
                        case UPDATED_BY:
                            return (
                                <div className={styles.updatedByContainer}>
                                    <p className={styles.updatedOn}>{formatUTCDateTime(record[UPDATED_ON] ?? "", false, true)}</p>
                                    <p className={styles.updatedBy}>{record[UPDATED_BY] ?? "N/A"}</p>
                                </div>
                            );

                        case USER_DEFINED_EMAIL_DESCRIPTION:
                            return isEditMode ? (
                                <Input
                                    ref={(el) => {
                                        emailDescRefs.current[id] = el;
                                    }}
                                    value={record[USER_DEFINED_EMAIL_DESCRIPTION] ?? ''}
                                    disabled={!record[INCLUDE_IN_EMAIL]}
                                    status={showEmailDescError ? 'error' : ''}
                                    onChange={(e) => onFieldChange(id, key, e.target.value || null)}
                                    size="middle"
                                />
                            ) : (
                                <span>{record[USER_DEFINED_EMAIL_DESCRIPTION] ?? '-'}</span>
                            );
                        
                        case USER_DEFINED_DESCRIPTION:
                            return isEditMode ? (
                                <Input
                                    ref={(el) => {
                                        portalDescRefs.current[id] = el;
                                    }}
                                    value={record[USER_DEFINED_DESCRIPTION] ?? ''}
                                    disabled={!record[INCLUDE_IN_REPORTING]}
                                    status={showPortalDescError ? 'error' : ''}
                                    onChange={(e) => onFieldChange(id, key, e.target.value || null)}
                                    size="middle"
                                />
                            ) : (
                                <span>{record[USER_DEFINED_DESCRIPTION] ?? '-'}</span>
                            );

                        case IS_REVIEW_COMPLETED:
                            return renderCheckbox(Boolean(value), isEditMode, (e) =>
                                onFieldChange(id, key, e.target.checked)
                            );
                    }

                    return <span>{value ?? '-'}</span>;
                }
            })
        }
    );